/**
 * Colourful gradient icon theme used across the public website.
 * Remix Icon glyphs are filled with a gradient (bg-clip-text), optionally on a soft pastel tile.
 *
 *   <GradientIcon icon="ri-coins-fill" tone="blue" />                 glyph only
 *   <GradientIcon icon="ri-gift-2-fill" tone="pink" variant="tile" />  glyph on a pastel tile
 */
export type IconTone = 'blue' | 'violet' | 'pink' | 'teal' | 'orange' | 'green' | 'cyan' | 'indigo';

// Full class strings so Tailwind can see them.
const GLYPH: Record<IconTone, string> = {
  blue: 'from-[#2f6bff] to-[#00b2ff]',
  violet: 'from-[#7b5cff] to-[#c25cff]',
  pink: 'from-[#ff4d8d] to-[#ff8a5c]',
  teal: 'from-[#00c2a8] to-[#00a0e9]',
  orange: 'from-[#ff9a1f] to-[#ff5c5c]',
  green: 'from-[#22c55e] to-[#14b8a6]',
  cyan: 'from-[#06b6d4] to-[#3b82f6]',
  indigo: 'from-[#2f51d6] to-[#7b5cff]',
};

const TILE: Record<IconTone, string> = {
  blue: 'bg-[#eaf1ff]',
  violet: 'bg-[#f1ecff]',
  pink: 'bg-[#ffedf3]',
  teal: 'bg-[#e6faf6]',
  orange: 'bg-[#fff3e6]',
  green: 'bg-[#e9faef]',
  cyan: 'bg-[#e6f8fc]',
  indigo: 'bg-[#ecefff]',
};

interface GradientIconProps {
  icon: string;
  tone?: IconTone;
  variant?: 'glyph' | 'tile';
  /** Glyph size, e.g. "text-2xl". */
  size?: string;
  className?: string;
}

export default function GradientIcon({ icon, tone = 'blue', variant = 'glyph', size = 'text-2xl', className = '' }: GradientIconProps) {
  const glyph = (
    <i
      aria-hidden="true"
      className={`${icon} ${size} leading-none bg-gradient-to-br ${GLYPH[tone]} bg-clip-text text-transparent`}
    />
  );
  if (variant === 'glyph') return <span className={`inline-flex items-center justify-center ${className}`}>{glyph}</span>;
  return (
    <span className={`inline-flex items-center justify-center rounded-xl ${TILE[tone]} ${className || 'w-11 h-11'}`}>
      {glyph}
    </span>
  );
}
